import React, { useEffect, useState } from "react";
import { getAuthToken } from "../../utils/authHelpers";
import { usePersona } from "../../contexts/PersonaContext";

/**
 * ApplyModal — the creator-facing "submit an application" sheet that
 * REPLACES the old "Request a Collab → straight into a DM" flow.
 *
 * Behavior:
 *   - POSTs to /api/collab/requests/create with channelId: null.
 *   - Does NOT create a Stream channel; the host opens it on approval.
 *   - Confirmation state shows "Application sent" inline; no redirect to /messages.
 *
 * Mobile pattern mirrors SummitPromoModal (full-height 100dvh card, X
 * pinned top-right with the 40x40 button + shadow so it never sits behind
 * iOS Safari's URL bar).
 */
const ApplyModal = ({
  isOpen,
  onClose,
  recipientUserId,
  recipientHandle, // for the confirmation copy
  targetKind, // 'property' | 'creator_profile' | 'host_profile' | 'other' | null
  targetId,
  title, // human label like "Chestnut Hill Cabin"
  collabDetails, // existing collab_details JSON snapshot
  onApplied, // optional callback after a successful submit
}) => {
  const [message, setMessage] = useState("");
  const [phase, setPhase] = useState("compose"); // 'compose' | 'sending' | 'sent' | 'error'
  const [errorMsg, setErrorMsg] = useState("");
  // Recorded on the request so merged host+creator accounts end up on the
  // side of the collab they were actually acting as when they applied.
  const { activePersona } = usePersona();

  useEffect(() => {
    if (!isOpen) return undefined;
    // Reset on every open so a previous error/success doesn't linger.
    setMessage("");
    setPhase("compose");
    setErrorMsg("");

    // Body scroll lock + Esc-to-close, matching SummitPromoModal pattern.
    if (typeof document === "undefined") return undefined;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKey = (e) => {
      if (e.key === "Escape" && phase !== "sending") onClose?.();
    };
    document.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener("keydown", onKey);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  if (!isOpen) return null;

  const handleSubmit = async (e) => {
    e?.preventDefault?.();
    if (phase === "sending") return;
    if (!recipientUserId) {
      setPhase("error");
      setErrorMsg("Missing recipient.");
      return;
    }

    const token = getAuthToken();
    if (!token) {
      setPhase("error");
      setErrorMsg("Please log in to apply.");
      return;
    }

    setPhase("sending");
    setErrorMsg("");

    try {
      const apiBase = window.location.hostname === "localhost" ? "http://localhost:4000" : "";
      // Splice the applicant's note into the snapshot so the host sees it
      // alongside the structured details. We never write it to a free-text
      // field that the LLM will read.
      const snapshot = {
        ...(collabDetails || {}),
        ...(title ? { title } : {}),
        ...(message?.trim() ? { applicationMessage: message.trim() } : {}),
      };
      const res = await fetch(`${apiBase}/api/collab/requests/create`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          recipientUserId,
          channelId: null, // <-- gate the DM; host has to approve first
          targetKind: targetKind || null,
          targetId: targetId || null,
          snapshot,
          ...(activePersona === "creator" || activePersona === "host"
            ? { requesterPersona: activePersona }
            : {}),
        }),
      });
      const data = await res.json();
      if (!res.ok || !data?.ok) {
        const code = data?.error || `http_${res.status}`;
        setPhase("error");
        setErrorMsg(prettyErrorForCode(code));
        return;
      }
      setPhase("sent");
      if (onApplied) onApplied({ requestId: data.requestId, existing: !!data.existing });
    } catch (err) {
      console.error("ApplyModal submit error:", err);
      setPhase("error");
      setErrorMsg("Couldn't reach Postaway. Please try again.");
    }
  };

  const recipientLabel = recipientHandle ? `@${String(recipientHandle).replace(/^@/, "")}` : "this host";

  return (
    <div
      className="apply-modal-backdrop"
      role="dialog"
      aria-modal="true"
      aria-labelledby="apply-modal-title"
      onClick={() => phase !== "sending" && onClose?.()}
    >
      <div className="apply-modal-card" onClick={(e) => e.stopPropagation()}>
        <button
          type="button"
          className="apply-modal-close"
          aria-label="Close"
          onClick={() => phase !== "sending" && onClose?.()}
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
            <line x1="18" y1="6" x2="6" y2="18" />
            <line x1="6" y1="6" x2="18" y2="18" />
          </svg>
        </button>

        <div className="apply-modal-scroll">
          <div className="apply-modal-body">
            {phase === "sent" ? (
              <SentState recipientLabel={recipientLabel} onClose={onClose} />
            ) : (
              <ComposeState
                title={title}
                recipientLabel={recipientLabel}
                collabDetails={collabDetails}
                message={message}
                setMessage={setMessage}
                onSubmit={handleSubmit}
                onClose={onClose}
                phase={phase}
                errorMsg={errorMsg}
              />
            )}
          </div>
        </div>
      </div>
      <ApplyModalStyles />
    </div>
  );
};

const ComposeState = ({
  title,
  recipientLabel,
  collabDetails,
  message,
  setMessage,
  onSubmit,
  onClose,
  phase,
  errorMsg,
}) => (
  <form onSubmit={onSubmit}>
    <div className="apply-modal-eyebrow">Apply to Collaborate</div>
    <h2 id="apply-modal-title" className="apply-modal-title">
      {title || "Send an application"}
    </h2>
    <div className="apply-modal-meta">
      You're applying with {recipientLabel}. They'll review and choose whether to chat.
    </div>

    {collabDetails && <CollabDetailsSummary details={collabDetails} />}

    <label htmlFor="apply-modal-message" className="apply-modal-label">
      A short note (optional)
    </label>
    <textarea
      id="apply-modal-message"
      className="apply-modal-textarea"
      placeholder="Tell the host why you're a great fit for this collaboration opportunity."
      value={message}
      onChange={(e) => setMessage(e.target.value)}
      rows={4}
      maxLength={600}
      disabled={phase === "sending"}
    />
    <div className="apply-modal-counter">{message.length}/600</div>

    {errorMsg && <div className="apply-modal-error">{errorMsg}</div>}

    <div className="apply-modal-actions">
      <button
        type="submit"
        className="apply-modal-btn primary"
        disabled={phase === "sending"}
      >
        {phase === "sending" ? "Sending…" : "Send application"}
      </button>
      <button
        type="button"
        className="apply-modal-btn secondary"
        onClick={onClose}
        disabled={phase === "sending"}
      >
        Cancel
      </button>
    </div>

    <div className="apply-modal-fineprint">
      No message is sent yet — the host has to approve before a chat opens. This keeps DMs clean for everyone.
      Your application is good for <strong>14 days</strong>: we'll remind the host to respond, and if they don't,
      we'll let you know and you can nudge them or reapply later.
    </div>
  </form>
);

const SentState = ({ recipientLabel, onClose }) => (
  <div className="apply-modal-sent">
    <div className="apply-modal-sent-check" aria-hidden>
      <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
        <polyline points="20 6 9 17 4 12" />
      </svg>
    </div>
    <h2 id="apply-modal-title" className="apply-modal-title">
      Application sent
    </h2>
    <p className="apply-modal-text">
      We've sent your application to {recipientLabel}. You'll get a notification the moment they
      open a chat, add you to their waitlist, or pass.
    </p>
    <p className="apply-modal-text" style={{ fontSize: "13px", color: "#6b7280" }}>
      Your application is good for 14 days. We'll remind the host to respond — if they don't,
      we'll let you know and you can nudge them or reapply another time.
    </p>
    <div className="apply-modal-actions">
      <button type="button" className="apply-modal-btn primary" onClick={onClose}>
        Done
      </button>
    </div>
  </div>
);

const CollabDetailsSummary = ({ details }) => {
  if (!details) return null;
  const rows = [];
  const dates = details.dates || {};
  if (dates.type === "flexible") rows.push(["Dates", "Flexible"]);
  else if (dates.start || dates.end) {
    const range = [dates.start, dates.end].filter(Boolean).join(" – ");
    if (range) rows.push(["Dates", range]);
  }
  if (details.compensation?.type) {
    const c = details.compensation;
    const compLabel = c.type === "paid_partnership"
      ? (c.budgetMin || c.budgetMax)
        ? `Paid · $${c.budgetMin || "?"}${c.budgetMax ? `–$${c.budgetMax}` : ""}`
        : "Paid partnership"
      : c.type.replace(/_/g, " ");
    rows.push(["Comp", compLabel]);
  }
  if (details.deliverables?.items?.length) {
    rows.push(["Deliverables", `${details.deliverables.items.length} item(s)`]);
  }
  if (!rows.length) return null;
  return (
    <div className="apply-modal-snapshot">
      {rows.map(([k, v]) => (
        <div key={k} className="apply-modal-snapshot-row">
          <span className="apply-modal-snapshot-key">{k}</span>
          <span className="apply-modal-snapshot-value">{v}</span>
        </div>
      ))}
    </div>
  );
};

function prettyErrorForCode(code) {
  switch (code) {
    case "cannot_request_self":
      return "You can't apply to your own listing.";
    case "recipient_not_found":
      return "That host is no longer available.";
    case "missing_recipient_user_id":
      return "Missing recipient.";
    case "unauthorized":
    case "invalid_token":
      return "Please log in again to apply.";
    default:
      return "Something went wrong. Please try again.";
  }
}

const ApplyModalStyles = () => (
  <style>{`
    .apply-modal-backdrop {
      position: fixed;
      inset: 0;
      z-index: 9999;
      background: rgba(15, 8, 75, 0.55);
      display: flex;
      align-items: center;
      justify-content: center;
      padding: 24px;
      box-sizing: border-box;
      animation: applyModalFadeIn 180ms ease-out;
      height: 100vh;
      height: 100dvh;
      overflow-y: auto;
    }
    @keyframes applyModalFadeIn { from { opacity: 0; } to { opacity: 1; } }
    .apply-modal-card {
      position: relative;
      background: #ffffff;
      border-radius: 18px;
      max-width: 520px;
      width: 100%;
      max-height: calc(100vh - 48px);
      max-height: calc(100dvh - 48px);
      display: flex;
      flex-direction: column;
      overflow: hidden;
      box-shadow: 0 24px 64px rgba(15, 8, 75, 0.35);
      animation: applyModalSlideIn 220ms cubic-bezier(0.2, 0.9, 0.3, 1);
      box-sizing: border-box;
    }
    .apply-modal-scroll {
      overflow-y: auto;
      -webkit-overflow-scrolling: touch;
      overscroll-behavior: contain;
    }
    @keyframes applyModalSlideIn {
      from { transform: translateY(20px); opacity: 0; }
      to { transform: translateY(0); opacity: 1; }
    }
    @media (max-width: 640px) {
      .apply-modal-backdrop { padding: 12px; }
      .apply-modal-card {
        max-height: calc(100vh - 24px);
        max-height: calc(100dvh - 24px);
        border-radius: 16px;
      }
    }
    .apply-modal-close {
      position: absolute;
      top: 12px;
      right: 12px;
      width: 40px;
      height: 40px;
      border-radius: 50%;
      border: none;
      background: rgba(255, 255, 255, 0.95);
      color: #0f084b;
      cursor: pointer;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      box-shadow: 0 2px 10px rgba(15, 8, 75, 0.25);
      transition: background 0.15s, transform 0.15s;
      z-index: 3;
    }
    .apply-modal-close:hover { background: #ffffff; transform: scale(1.05); }
    .apply-modal-body {
      padding: 30px 26px 26px;
      display: flex;
      flex-direction: column;
      gap: 10px;
    }
    @media (max-width: 640px) {
      .apply-modal-body { padding: 26px 18px 22px; gap: 8px; }
    }
    .apply-modal-eyebrow {
      font-family: 'Neue Einstellung', 'Poppins', sans-serif;
      font-size: 11px;
      font-weight: 700;
      letter-spacing: 0.2em;
      text-transform: uppercase;
      color: #dc1d08;
    }
    .apply-modal-title {
      font-family: 'Sailors', serif;
      font-size: 26px;
      color: #0f084b;
      margin: 0;
      line-height: 1.15;
    }
    @media (max-width: 640px) {
      .apply-modal-title { font-size: 22px; }
    }
    .apply-modal-meta {
      font-family: 'Neue Einstellung', 'Poppins', sans-serif;
      font-size: 13px;
      color: #6b7280;
      margin-bottom: 6px;
    }
    .apply-modal-text {
      font-family: 'Neue Einstellung', 'Poppins', sans-serif;
      font-size: 14.5px;
      line-height: 1.55;
      color: #353945;
      margin: 6px 0 0;
    }
    .apply-modal-snapshot {
      background: #f8f7fd;
      border: 1px solid #e8e6f4;
      border-radius: 12px;
      padding: 10px 14px;
      margin: 8px 0 14px;
      display: flex;
      flex-direction: column;
      gap: 4px;
    }
    .apply-modal-snapshot-row {
      display: flex;
      gap: 8px;
      font-family: 'Neue Einstellung', 'Poppins', sans-serif;
      font-size: 13px;
    }
    .apply-modal-snapshot-key {
      color: #6b7280;
      min-width: 92px;
      font-weight: 600;
    }
    .apply-modal-snapshot-value {
      color: #0f084b;
      font-weight: 500;
    }
    .apply-modal-label {
      font-family: 'Neue Einstellung', 'Poppins', sans-serif;
      font-size: 13px;
      font-weight: 600;
      color: #0f084b;
      margin-top: 6px;
    }
    .apply-modal-textarea {
      width: 100%;
      box-sizing: border-box;
      padding: 12px 14px;
      border-radius: 10px;
      border: 1.5px solid #e8e6f4;
      font-family: 'Neue Einstellung', 'Poppins', sans-serif;
      font-size: 14.5px;
      color: #0f084b;
      resize: vertical;
      min-height: 96px;
      outline: none;
      transition: border-color 0.15s;
    }
    .apply-modal-textarea:focus { border-color: #0f084b; }
    .apply-modal-counter {
      align-self: flex-end;
      font-family: 'Neue Einstellung', 'Poppins', sans-serif;
      font-size: 11px;
      color: #9ca3af;
    }
    .apply-modal-error {
      margin-top: 6px;
      padding: 10px 12px;
      border-radius: 10px;
      background: rgba(220, 29, 8, 0.08);
      color: #b51706;
      font-family: 'Neue Einstellung', 'Poppins', sans-serif;
      font-size: 13px;
    }
    .apply-modal-actions {
      display: flex;
      gap: 10px;
      margin-top: 14px;
      flex-wrap: wrap;
    }
    @media (max-width: 480px) {
      .apply-modal-actions { flex-direction: column-reverse; gap: 8px; }
    }
    .apply-modal-btn {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      padding: 11px 22px;
      border-radius: 100px;
      font-family: 'Neue Einstellung', 'Poppins', sans-serif;
      font-size: 14.5px;
      font-weight: 600;
      text-decoration: none;
      border: 2px solid transparent;
      cursor: pointer;
      transition: all 0.18s;
      flex: 1;
      min-height: 44px;
    }
    @media (max-width: 480px) {
      .apply-modal-btn { width: 100%; flex: 0 0 auto; }
    }
    .apply-modal-btn.primary {
      background: #dc1d08;
      color: #ffffff;
      border-color: #dc1d08;
    }
    .apply-modal-btn.primary:hover { background: #b51706; border-color: #b51706; }
    .apply-modal-btn.primary:disabled { background: #e89186; border-color: #e89186; cursor: default; }
    .apply-modal-btn.secondary {
      background: transparent;
      color: #0f084b;
      border-color: #0f084b;
    }
    .apply-modal-btn.secondary:hover:not(:disabled) { background: #0f084b; color: #ffffff; }
    .apply-modal-btn.secondary:disabled { color: #9ca3af; border-color: #d8d6e4; cursor: default; }
    .apply-modal-fineprint {
      margin-top: 12px;
      font-family: 'Neue Einstellung', 'Poppins', sans-serif;
      font-size: 12px;
      color: #6b7280;
      line-height: 1.45;
    }
    .apply-modal-sent {
      display: flex;
      flex-direction: column;
      align-items: center;
      text-align: center;
      gap: 10px;
      padding: 8px 0 4px;
    }
    .apply-modal-sent-check {
      width: 64px;
      height: 64px;
      border-radius: 50%;
      background: #ecfdf5;
      color: #047857;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      margin-bottom: 6px;
    }
  `}</style>
);

export default ApplyModal;
